import db from './db'
import helpers from '../helpers'

// One-time starter content for a fresh install: two of the user's own lists
// pre-filled with sample items, so the app isn't empty on first open. Kept
// deliberately small — the home grid pairs them with a "New list" circle, and
// richer starting points (travel, camping, parties) now live as public lists
// the user can browse and copy (see services/public-lists.ts). They live
// only in IndexedDB (synced:false) and are pushed to the server lazily on the
// user's first edit (see list.ts flush()). Lists get real UUIDs and items get
// temp_ ids, exactly like anything created offline, so the normal sync path
// applies and nothing collides on the server.

const SEED_FLAG = 'papa-seeded'

// Set only when starter lists were actually written (not for existing installs),
// so the home page knows to show the first-run welcome that explains them.
// Cleared when the user dismisses the welcome (see app/pages/home/welcome).
export const WELCOME_FLAG = 'papa-welcome'

// `color` is the palette slot (hue) each starter should show — chosen to look good
// in the dark theme. See app/configs/colors.ts (the two palettes are index-aligned).
// `done` is how many of the items start checked off (the first N), so a fresh install
// shows some progress on the circles instead of every list reading 0 done.
const STARTER: { name: string; items: string[]; color: number; done: number }[] = [
    { name: 'Groceries', color: 6, done: 2, items: ['Milk', 'Eggs', 'Bread', 'Bananas'] },         // sage  rgba(36,90,68)
    { name: 'To-Do', color: 12, done: 1, items: ['Pay bills', 'Book dentist', 'Water the plants'] }, // cyan  rgba(31,110,112)
]

const tempId = (n: number): string =>
    'temp_' + Date.now() + '_' + n + '_' + Math.random().toString(36).substring(2, 7)

// A random (server-safe, unique-per-install) UUID whose hash lands on the wanted
// palette slot, so a starter renders in the chosen color without hardcoding a shared id.
const idForColor = (color: number): string => {
    for (let i = 0; i < 500; i++) {
        const id = crypto.randomUUID()
        if (helpers.paletteIndexForId(id) === color) return id
    }
    return crypto.randomUUID() // give up after 500 tries (effectively never happens)
}

let promise: Promise<void> | null = null

// Idempotent + memoized: safe to call at boot and again before the first list read.
const run = (): Promise<void> => {
    if (!promise) promise = doSeed()
    return promise
}

const doSeed = async (): Promise<void> => {
    await db.init() // ensure the stores exist before writing
    if (localStorage.getItem(SEED_FLAG)) return

    // Only seed a genuinely fresh install — never inject starter lists over an
    // install that already has lists (e.g. an existing user updating the app).
    const existing = await db.all(db.LIST_STORE)
    if (existing.length) {
        localStorage.setItem(SEED_FLAG, '1')
        return
    }

    localStorage.setItem(SEED_FLAG, '1') // set up front so a fast reload can't double-seed

    try {
        let order = 0
        for (const starter of STARTER) {
            const listId = idForColor(starter.color)
            await db.save(db.LIST_STORE, {
                id: listId,
                name: starter.name,
                created_by: '',
                created_at: new Date(Date.now() - order * 1000).toISOString(),
                role: 'owner',
                item_count: starter.items.length,
                done_count: starter.done,
                member_count: 1,
                synced: false,
            })

            for (let i = 0; i < starter.items.length; i++) {
                // Stagger created_at so items keep their listed order (newest-first sort).
                const ts = new Date(Date.now() - order * 1000).toISOString()
                order++
                await db.save(db.STORE, {
                    id: tempId(order),
                    list_id: listId,
                    name: starter.items[i],
                    checked: i < starter.done, // the first `done` items start ticked off
                    category: 'General',
                    user_id: '',
                    synced: false,
                    created_at: ts,
                    updated_at: ts,
                })
            }
        }
        localStorage.setItem(WELCOME_FLAG, '1')
    } catch (err) {
        console.warn('Seeding starter lists failed:', err)
        localStorage.removeItem(SEED_FLAG) // allow a retry on next launch
    }
}

export default { run }
